import { useCart } from "@/context/cart";
import { deleteCartItem, updateCartItem } from "@/services/cart";
import { getCookies, setCookie } from "cookies-next";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "next-i18next";
import { priceFormattedObject, validatePrice } from "@/helpers/product";
import Remove from "@/components/icons/Remove";
import { handlePriceByComma } from "@/helpers/common/handlePrice";
import { validateImgSrc } from "@/utils/global";
import NextImage from "@/components/global/NextImage";
import CurrencyIcon from "@/components/icons/CurrencyIcon";
import { event_remove_from_cart } from "@/utils/gtm";

const CartDropDownItem = ({ product }) => {
  const [loadingBtn, setLoadingBtn] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setQty(product?.qty);
  }, [product]);

  const { dispatch } = useCart();
  const { locale } = useRouter();
  const { handshake, mage, quote } = getCookies();
  const { t } = useTranslation("common");

  const price = validatePrice(priceFormattedObject(product));

  const handleRemoveItem = async (product) => {
    setLoadingBtn("deleting");
    const res = await deleteCartItem(
      handshake,
      mage,
      quote,
      product?.item_id || product?.data?.item_id
    );

    if (res?.status === 404) {
      dispatch({
        type: "remove",
        payload: { item_id: product?.item_id || product?.data?.item_id },
      });
    }

    if (res?.data?.quoteId !== undefined && res?.data?.quoteId != quote) {
      setCookie("quote", res?.data?.quoteId);
      dispatch({ type: "clear" });
    }

    if (res?.status === 200) {
      dispatch({
        type: "remove",
        payload: {
          item_id: product?.item_id || product?.data?.item_id,
          from_mini_cart: true,
        },
      });
      event_remove_from_cart(product);
      toast.success(
        locale === "en"
          ? "Item Deleted From Cart"
          : "تم إزالة المنتج من عربة التسوق"
      );
      setLoadingBtn("");
    } else {
      toast.error(
        locale === "en"
          ? "Error Occured, please try again"
          : "حدث خطأ, برجاء إعادة المحاولة"
      );
      setLoadingBtn("");
    }
  };

  const handleUpdateItem = async (product, qty) => {
    setLoadingBtn("updating");
    const res = await updateCartItem(
      handshake,
      mage,
      quote,
      product?.item_id,
      qty
    );

    if (res?.status === 404) {
      dispatch({
        type: "remove",
        payload: { item_id: product?.item_id || product?.data?.item_id },
      });
    }

    if (res?.data?.quoteId !== undefined && res?.data?.quoteId != quote) {
      setCookie("quote", res?.data?.quoteId);
      dispatch({ type: "clear" });
    }

    if (res?.status === 200) {
      toast.success(
        locale === "en"
          ? "Item Updated Successfully"
          : "تم تعديل كمية المنتج بنجاح"
      );
      dispatch({
        type: "update",
        payload: { item_id: product?.item_id, qty, from_mini_cart: true },
      });
      setLoadingBtn("");
    } else {
      toast.error(
        res?.error?.error?.message
          ? t(res?.error?.error?.message)
          : locale === "en"
          ? "Error Occured, please try again"
          : "حذث خطأ, برجاء المحاولة مره اخرى"
      );
      if (
        res?.error?.error?.message ===
        "There are no source items with the in stock status"
      ) {
        await handleRemoveItem(product);
      }
      setLoadingBtn("");
    }
  };

  return (
    <li className="flex gap-2 border-b py-3.5 first:pt-0 last:pb-1 last:border-b-0">
      <NextImage
        src={validateImgSrc(
          `${process.env.NEXT_PUBLIC_IMG_BASEURL}/catalog/product${
            product?.image
              ? product?.image?.[0]
              : product?.extension_attributes?.product_image
          }`
        )}
        width={96}
        height={96}
        className="object-contain border rounded-md size-24"
        alt={product?.name}
      />
      <div className="flex flex-col text-[15px] flex-1">
        <div className="flex justify-between">
          <p className="font-semibold">{product?.name}</p>

          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveItem(product);
            }}
            disabled={loadingBtn === "deleting"}
            className="flex items-center justify-center disabled:cursor-not-allowed"
          >
            {loadingBtn === "deleting" ? (
              <span className="flex items-center justify-center w-6 loader"></span>
            ) : (
              <Remove className="mt-1 size-5" />
            )}
          </button>
        </div>
        <p className="flex items-center gap-1 font-bold">
          {handlePriceByComma(price?.specialPrice || price?.originalPrice)}{" "}
          <CurrencyIcon className="size-4" />
        </p>
        <div className="flex items-center justify-between">
          <span className="">{locale === "en" ? "Qty" : "الكمية"}</span>
          <div className="flex items-center gap-2">
            <select
              className="px-1 border rounded-md outline-none cursor-pointer"
              id=""
              defaultValue={product?.qty}
              value={qty}
              onChange={(e) => {
                setQty(e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {[...Array(10).keys()].map((i) => (
                <option value={i + 1} key={i}>
                  {i + 1}
                </option>
              ))}
            </select>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleUpdateItem(product, qty);
              }}
              disabled={loadingBtn === "updating"}
              className="flex items-center justify-center gap-1 px-2 py-1 font-semibold text-white rounded-md bg-tertiary-800 disabled:cursor-not-allowed disabled:opacity-75"
            >
              {loadingBtn === "updating" && (
                <span className="flex items-center justify-center w-6 loader"></span>
              )}
              {locale === "en" ? "Update" : "تعديل"}
            </button>
          </div>
        </div>
      </div>
    </li>
  );
};

export default CartDropDownItem;
